import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Descriptions, Space, Table, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { formatMoney } from '@/utils/format';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { getEst3CostProvenance } from '../est3CostProvenance';
import { EpcEstimatingCostingPanel } from './EpcEstimatingCostingPanel';
import { isPhase2HealthCode } from './costingMethodHelpers';
import styles from './EpcEstimatingWorkbench.module.css';

const { Text, Paragraph } = Typography;

export interface EpcEstimatingWorkbenchInspectorProps {
  estimateId: string;
  revisionId: string;
  selectedBoqLineId: string | null;
  costLines: any[];
  readOnly?: boolean;
  onAddCost?: (boqLineId: string) => void;
  onOpenCostDetail?: (line: Record<string, unknown>) => void;
  onOpenBuildUp?: (line: Record<string, unknown>) => void;
  /** After costing mutations — parent reloads BOQ / cost / diagnostics. */
  onMutated?: () => void;
  /** Optional line-scoped health findings for COSTING / Overview. */
  healthIssues?: Array<{
    code?: string;
    severity?: string;
    message?: string;
    client_boq_line_id?: string;
    boq_line_id?: string;
    entity_id?: string;
  }>;
}

type InspectorTab = 'costing' | 'overview' | 'buildup' | 'provenance' | 'health';

export const EpcEstimatingWorkbenchInspector: React.FC<EpcEstimatingWorkbenchInspectorProps> = ({
  estimateId,
  revisionId,
  selectedBoqLineId,
  costLines,
  readOnly,
  onAddCost,
  onOpenCostDetail,
  onOpenBuildUp,
  onMutated,
  healthIssues,
}) => {
  const [tab, setTab] = useState<InspectorTab>('costing');

  useEffect(() => {
    if (selectedBoqLineId) setTab('costing');
  }, [selectedBoqLineId]);

  const detailQ = useQuery({
    queryKey: ['epc-est-boq-detail', estimateId, revisionId, selectedBoqLineId],
    queryFn: () =>
      epcEstimationService.getClientBoqLineDetail(estimateId, revisionId, selectedBoqLineId!),
    enabled: !!estimateId && !!revisionId && !!selectedBoqLineId,
  });

  const linked = useMemo(() => {
    if (detailQ.data?.linked_cost_lines) return detailQ.data.linked_cost_lines as any[];
    if (!selectedBoqLineId) return [];
    return (costLines || []).filter((c) => c.client_boq_line_id === selectedBoqLineId);
  }, [detailQ.data, costLines, selectedBoqLineId]);

  const lineHealth = useMemo(() => {
    if (!selectedBoqLineId || !healthIssues?.length) return [];
    return healthIssues.filter((h) => {
      const id = h.client_boq_line_id || h.boq_line_id || h.entity_id;
      return id && String(id) === selectedBoqLineId;
    });
  }, [healthIssues, selectedBoqLineId]);

  const boq = detailQ.data?.boq_line;
  const costTotal =
    detailQ.data?.cost_total ??
    linked.reduce((a, c) => a + Number(c.total_amount || 0), 0);

  if (!selectedBoqLineId) {
    return (
      <aside className={styles.inspector}>
        <div className={styles.inspectorHeader}>Context</div>
        <div className={styles.inspectorBody}>
          <Alert
            type="info"
            showIcon
            message="Select a BOQ line"
            description="COSTING opens method → assembly → rates → provenance without leaving the Workbench."
          />
        </div>
      </aside>
    );
  }

  return (
    <aside className={styles.inspector} data-testid="estimating-workbench-inspector">
      <div className={styles.inspectorHeader}>
        <Text ellipsis style={{ maxWidth: 320 }}>
          {boq?.item_number ? `${boq.item_number} · ` : ''}
          {boq?.description || 'BOQ line'}
        </Text>
      </div>
      <div style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0' }}>
        <Space size={4} wrap>
          <Button
            size="small"
            type={tab === 'costing' ? 'primary' : 'default'}
            onClick={() => setTab('costing')}
            data-testid="inspector-tab-costing"
          >
            Costing
          </Button>
          <Button
            size="small"
            type={tab === 'overview' ? 'primary' : 'default'}
            onClick={() => setTab('overview')}
          >
            Overview
          </Button>
          <Button
            size="small"
            type={tab === 'buildup' ? 'primary' : 'default'}
            onClick={() => setTab('buildup')}
          >
            Build-up
          </Button>
          <Button
            size="small"
            type={tab === 'provenance' ? 'primary' : 'default'}
            onClick={() => setTab('provenance')}
          >
            Provenance
          </Button>
          {lineHealth.length ? (
            <Button
              size="small"
              type={tab === 'health' ? 'primary' : 'default'}
              danger={lineHealth.some(
                (h) => h.severity === 'error' || h.severity === 'BLOCKER'
              )}
              onClick={() => setTab('health')}
            >
              Health · {lineHealth.length}
            </Button>
          ) : null}
        </Space>
      </div>
      <div className={styles.inspectorBody}>
        {tab === 'costing' ? (
          <EpcEstimatingCostingPanel
            estimateId={estimateId}
            revisionId={revisionId}
            selectedBoqLineId={selectedBoqLineId}
            costLines={costLines}
            readOnly={readOnly}
            onMutated={onMutated}
          />
        ) : null}

        {tab === 'overview' ? (
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Item">{boq?.item_number || '—'}</Descriptions.Item>
              <Descriptions.Item label="UOM">{boq?.uom || '—'}</Descriptions.Item>
              <Descriptions.Item label="Qty">{boq?.quantity ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Schedule">
                {formatMoney(boq?.amount, { display: 'plain', maximumFractionDigits: 0 })}
              </Descriptions.Item>
              <Descriptions.Item label="Cost">
                {formatMoney(costTotal, { display: 'plain', maximumFractionDigits: 0 })}
              </Descriptions.Item>
              <Descriptions.Item label="Sell">
                {formatMoney(detailQ.data?.sell_amount ?? boq?.sell_amount, {
                  display: 'plain',
                  maximumFractionDigits: 0,
                })}
              </Descriptions.Item>
              <Descriptions.Item label="Margin">
                {detailQ.data?.margin_pct != null ? `${detailQ.data.margin_pct}%` : '—'}
              </Descriptions.Item>
            </Descriptions>
            {!readOnly && onAddCost ? (
              <Button type="primary" size="small" block onClick={() => onAddCost(selectedBoqLineId)}>
                Add cost line
              </Button>
            ) : null}
            <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
              Totals shown after save come from backend TEC / pricing — not a browser calculation
              engine.
            </Paragraph>
          </Space>
        ) : null}

        {tab === 'buildup' ? (
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Text type="secondary">{linked.length} linked cost line(s)</Text>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={linked}
              onRow={(row) => ({
                onClick: () => onOpenCostDetail?.(row),
                style: { cursor: 'pointer' },
              })}
              columns={[
                { title: 'Kind', dataIndex: 'resource_kind', width: 88 },
                { title: 'Description', dataIndex: 'description', ellipsis: true },
                {
                  title: 'Total',
                  dataIndex: 'total_amount',
                  width: 80,
                  render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 0 }),
                },
                {
                  title: '',
                  width: 64,
                  render: (_: unknown, row: any) => (
                    <Button
                      type="link"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenBuildUp?.(row);
                      }}
                    >
                      Open
                    </Button>
                  ),
                },
              ]}
            />
          </Space>
        ) : null}

        {tab === 'provenance' ? (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {!linked.length ? (
              <Alert type="warning" showIcon message="No cost lines — provenance unavailable" />
            ) : (
              linked.map((line: any) => {
                const p = getEst3CostProvenance(line);
                return (
                  <div key={line.id} style={{ border: '1px solid #f0f0f0', borderRadius: 4, padding: 8 }}>
                    <Text strong style={{ fontSize: 12 }}>
                      {line.resource_kind} · {line.description || 'Cost line'}
                    </Text>
                    <Descriptions size="small" column={1} style={{ marginTop: 6 }}>
                      <Descriptions.Item label="Rate">
                        {formatMoney(line.rate, { display: 'plain', maximumFractionDigits: 4 })}
                      </Descriptions.Item>
                      <Descriptions.Item label="Currency">{line.currency || '—'}</Descriptions.Item>
                      <Descriptions.Item label="UOM">{line.uom || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Source type">
                        {p.rateSource || 'Not available'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Reference">
                        {p.sourceRefLabel || p.sourceRefType || '—'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Quote date">{p.quoteDate || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Summary">
                        <Tag>{p.summary}</Tag>
                      </Descriptions.Item>
                    </Descriptions>
                  </div>
                );
              })
            )}
          </Space>
        ) : null}

        {tab === 'health' ? (
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            {!lineHealth.length ? (
              <Text type="secondary">No line-scoped diagnostics.</Text>
            ) : (
              lineHealth.map((h, i) => (
                <div
                  key={`${h.code}-${i}`}
                  style={{ border: '1px solid #f0f0f0', borderRadius: 4, padding: 8 }}
                >
                  <Space wrap size={4}>
                    <Tag color={h.severity === 'error' || h.severity === 'BLOCKER' ? 'red' : 'orange'}>
                      {h.severity || 'info'}
                    </Tag>
                    <Text code>{h.code}</Text>
                    {isPhase2HealthCode(h.code) ? <Tag color="purple">P2</Tag> : null}
                  </Space>
                  <div style={{ marginTop: 4 }}>{h.message}</div>
                </div>
              ))
            )}
          </Space>
        ) : null}
      </div>
    </aside>
  );
};
